import { RootState } from 'app/store';
import { ExistingSystemType } from 'data/types';
import { TonnageSeedRange } from 'features/ui/tonnage-filter-seed-types';
import { selectEligibleDuctedSystems } from 'utils/ducted-design';
import { selectDuctedPairContextBySystemId } from 'utils/ducted-pair-context';
import { DuctedPairContextState } from 'utils/ducted-pair-context-types';
import { getChooseSystemsEligibleDuctedSystems } from 'utils/existing-equipment';
import { resolveSystemTonnageSeed } from 'utils/system-tonnage-seed';
import { resolveTonnageSeed } from 'utils/tonnage-logic';

type AnchorInputs = Pick<
  RootState['structure'],
  | 'existingSystems'
  | 'recommendationSelectionsBySystem'
  | 'wantCompositionPicksBySystem'
>;

// Tonnage anchors are ducted only: the PTAC anchor is eligible for the
// Choose Systems pipeline but sizes by BTU class, never on the tonnage scale.
export function findEligibleAnchorDuctedSystem({
  existingSystems,
  recommendationSelectionsBySystem,
  wantCompositionPicksBySystem
}: AnchorInputs): ExistingSystemType | null {
  return (
    getChooseSystemsEligibleDuctedSystems(
      existingSystems,
      recommendationSelectionsBySystem,
      wantCompositionPicksBySystem
    ).find((system) => system.type === 'ducted') ?? null
  );
}

export function findAnchorDuctedSystem(
  inputs: AnchorInputs
): ExistingSystemType | null {
  return (
    findEligibleAnchorDuctedSystem(inputs) ??
    inputs.existingSystems.find((candidate) => candidate.type === 'ducted') ??
    null
  );
}

function isTonnageSeed(
  seed: TonnageSeedRange | null
): seed is TonnageSeedRange {
  return seed !== null;
}

function unionTonnageSeeds(seeds: TonnageSeedRange[]): TonnageSeedRange {
  return seeds.reduce((union, seed) => ({
    min: Math.min(union.min, seed.min),
    max: Math.max(union.max, seed.max)
  }));
}

function resolveLinkedPairTonnageSeeds(
  state: DuctedPairContextState
): Array<TonnageSeedRange | null> {
  const contexts = selectDuctedPairContextBySystemId(state);
  return selectEligibleDuctedSystems(state)
    .filter((system) => contexts[system.uniqueId] !== undefined)
    .map((system) =>
      resolveSystemTonnageSeed({
        system,
        record:
          state.structure.recommendationSelectionsBySystem[system.uniqueId]
            ?.tonnage ?? null,
        coolingBtu: contexts[system.uniqueId].loads.cooling
      })
    );
}

export function resolveStructureTonnageSeed(
  state: DuctedPairContextState
): TonnageSeedRange | null {
  if (!state.structure.recommendationSelectionsLoaded) return null;
  const pairSeeds = resolveLinkedPairTonnageSeeds(state);
  if (pairSeeds.length > 0) {
    return pairSeeds.every(isTonnageSeed) ? unionTonnageSeeds(pairSeeds) : null;
  }
  const system = findEligibleAnchorDuctedSystem(state.structure);
  if (!system) return null;
  return resolveTonnageSeed({
    record:
      state.structure.recommendationSelectionsBySystem[system.uniqueId]
        ?.tonnage ?? null,
    components: system.components,
    coolingBtu: state.project.totalBTUs.cooling
  });
}
